import React, { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * FOC-203 — shows the tenant's server-rendered result page (frame + card) and mounts
 * the app's own components into its system slots:
 *   hero  → the artwork hero (image load/error handling, blurred backdrop, overlay)
 *   tools → the language picker + Share
 *   body  → the object's interactive content blocks
 * The HTML is already sanitized server-side (server/page-render/renderPage.ts); slot
 * markers are inserted there after sanitizing, so a template can't forge one.
 */

type SlotName = "hero" | "tools" | "body";
const SLOTS: SlotName[] = ["hero", "tools", "body"];
// Keep in step with SLOT_SELECTOR in server/page-render/renderPage.ts.
const slotSelector = (name: SlotName) => `.focus-slot[data-slot="${name}"]`;

type Props = {
  html: string;
  hero: ReactNode;
  tools: ReactNode;
  body: ReactNode;
};

export const TemplatedResult: React.FC<Props> = ({
  html,
  hero,
  tools,
  body,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [targets, setTargets] = useState<Partial<Record<SlotName, Element>>>(
    {}
  );

  // After the HTML is in the DOM, find each slot marker. Re-run only when the HTML
  // changes (React leaves innerHTML alone otherwise, so the portals stay mounted).
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const found: Partial<Record<SlotName, Element>> = {};
    for (const name of SLOTS) {
      const el = root.querySelector(slotSelector(name));
      if (el) found[name] = el;
    }
    setTargets(found);
  }, [html]);

  const content: Record<SlotName, ReactNode> = { hero, tools, body };

  return (
    <>
      <div
        ref={rootRef}
        className="templated-result"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {SLOTS.map((name) =>
        targets[name] ? (
          <React.Fragment key={name}>
            {createPortal(content[name], targets[name] as Element)}
          </React.Fragment>
        ) : null
      )}
    </>
  );
};
